/**
 * src/services/spatial/geoLinkOrganizationMirrorClient.ts
 *
 * GeoLink's organization mirror — `/api/admin/organizations`.
 *
 * The chain is authoritative for what an organization *is*
 * (`geo-org-registry`). This mirror exists so GeoLink can enforce the
 * `spatial_objects.organization_id` foreign key and so anything can
 * enumerate organizations at all, which the registry cannot do: it is a
 * key/value store with no index.
 *
 * Two rules from GeoLink's handoff that shape this module:
 *
 * 1. **Write ordering.** Submit the transaction, wait for confirmation,
 *    then push the mirror with the resulting `chain_ledger`. Never push
 *    first. A mirror behind the chain is staleness that the next push
 *    fixes; a mirror ahead of it is a phantom row nothing will ever
 *    reconcile away, indistinguishable from one written by a buggy
 *    client.
 * 2. **Never an authorization source.** Read it for listings and UI
 *    scoping. Do not authorize a state-changing action from it — it can
 *    lag a revocation, and that lag is exactly when a revoked operator
 *    will try to act.
 *
 * Auth is the `organizations:write` scope on the API key GeoTrust's
 * proxy already injects. The scope is granted per key by someone with
 * database access on the GeoLink deployment, so a fresh environment
 * answers writes with 403 until that happens — `MirrorNotProvisioned`
 * below distinguishes that from a bad key.
 */

import { getGeoLinkApiBaseUrl } from './spatialEnv';
import { organizationMirrorAvailable } from './geoLinkCapabilities';

// ─── Types ────────────────────────────────────────────────────────────

export interface MirroredOrganization {
  id: string;
  name: string | null;
  countryCode: string | null;
  status: string | null;
  verifiedAt?: string | null;
  chainLedger?: number | null;
  chainSyncedAt?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface MirrorUpsert {
  id: string;
  name: string;
  countryCode: string;
  status: string;
  /**
   * Ledger the registration or mutation confirmed in. GeoLink rejects a
   * scoped-key `PUT` without it (400) so every automated write carries
   * the provenance a future reconciler needs. Admin JWTs may omit it;
   * we always have it, because we just submitted the transaction.
   */
  chainLedger: number;
  metadata?: Record<string, unknown>;
}

export interface MirrorListQuery {
  countryCode?: string;
  status?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

/**
 * The deployment has the mirror but this key has not been granted
 * `organizations:write`. An operator with database access must run
 * `grant-api-key-scope.js`. Distinct from a bad key, which is a 401.
 */
export class MirrorNotProvisionedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MirrorNotProvisionedError';
  }
}

// ─── HTTP ─────────────────────────────────────────────────────────────

function baseUrl(): string {
  return getGeoLinkApiBaseUrl().replace(/\/+$/, '');
}

// No auth header: the backend proxy injects the server-side key.
function authHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
  };
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${baseUrl()}/api/admin/organizations${path}`, {
    ...init,
    headers: { ...authHeaders(), ...(init.headers as Record<string, string> | undefined) },
  });

  const text = await res.text();
  let body: unknown = null;
  if (text) { try { body = JSON.parse(text); } catch { /* leave as text */ } }

  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; message?: string };
    const msg = b.error || b.message || `${res.status} ${res.statusText}`;
    if (res.status === 403) {
      throw new MirrorNotProvisionedError(
        `GeoLink has not granted this API key the organizations:write scope. ` +
        `Run: node scripts/grant-api-key-scope.js --key <geotrust-key> ` +
        `--scope organizations:write  (GeoLink said: ${msg})`,
      );
    }
    const err = new Error(`[geoLinkOrgMirror] ${path || '/'}: ${msg}`);
    (err as { status?: number }).status = res.status;
    throw err;
  }
  return (body as T) ?? (undefined as unknown as T);
}

// ─── Reads ────────────────────────────────────────────────────────────

export interface MirrorListResult {
  organizations: MirroredOrganization[];
  total: number;
}

export async function listMirroredOrganizations(
  query: MirrorListQuery = {},
): Promise<MirrorListResult> {
  const qs = new URLSearchParams();
  if (query.countryCode) qs.set('country_code', query.countryCode.toUpperCase());
  if (query.status)      qs.set('status', query.status);
  if (query.q)           qs.set('q', query.q);
  if (query.limit  != null) qs.set('limit',  String(query.limit));
  if (query.offset != null) qs.set('offset', String(query.offset));

  const json = await call<{
    total?: number;
    count?: number;
    organizations?: MirroredOrganization[];
  }>(qs.toString() ? `?${qs.toString()}` : '');

  const organizations = json.organizations ?? [];
  return { organizations, total: json.total ?? organizations.length };
}

// ─── Writes ───────────────────────────────────────────────────────────

/**
 * Idempotent upsert keyed by the on-chain id. Safe to call on every
 * write rather than tracking which organizations are already mirrored.
 *
 * Call this only AFTER the registration transaction has confirmed —
 * see the module header on write ordering.
 */
export async function pushOrganizationToMirror(
  org: MirrorUpsert,
): Promise<MirroredOrganization> {
  const json = await call<{ organization?: MirroredOrganization } | MirroredOrganization>(
    `/${org.id.toLowerCase()}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        name:          org.name,
        country_code:  org.countryCode.toUpperCase(),
        status:        org.status,
        chain_ledger:  org.chainLedger,
        ...(org.metadata ? { metadata: org.metadata } : {}),
      }),
    },
  );
  return (json as { organization?: MirroredOrganization }).organization
    ?? (json as MirroredOrganization);
}

/**
 * Best-effort mirror push.
 *
 * Used on the path right after a confirmed on-chain write, where the
 * transaction has already succeeded and there is nothing to roll back.
 * Reporting "registration failed" because a mirror row did not land
 * would be a lie, so failures are returned for the caller to surface as
 * a warning instead of thrown.
 */
export async function tryPushOrganizationToMirror(
  org: MirrorUpsert,
): Promise<{ mirrored: boolean; reason?: string }> {
  if (!(await organizationMirrorAvailable())) {
    return { mirrored: false, reason: 'This GeoLink deployment has no organization mirror.' };
  }
  try {
    await pushOrganizationToMirror(org);
    return { mirrored: true };
  } catch (e) {
    return { mirrored: false, reason: e instanceof Error ? e.message : String(e) };
  }
}
